import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

export interface ChartSeries {
  label: string;
  /** `[unix seconds, value]`; null is a gap. */
  points: [number, number | null][];
}

/** Series colours by position, from the design tokens. */
const SERIES_TOKENS = ["--accent", "--info", "--warn"];
const HEIGHT = 180;

const token = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888";

/** Bumps when the theme changes (system preference or the `data-theme` choice), to redraw. */
function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", bump);
    const observer = new MutationObserver(bump);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      media.removeEventListener("change", bump);
      observer.disconnect();
    };
  }, []);
  return version;
}

/** One x axis for all series (they come from one provider answer), values aligned by timestamp. */
function align(series: ChartSeries[]): uPlot.AlignedData {
  const xs = [...new Set(series.flatMap((s) => s.points.map(([ts]) => ts)))].sort((a, b) => a - b);
  const ys = series.map((s) => {
    const byTs = new Map(s.points);
    return xs.map((ts) => byTs.get(ts) ?? null);
  });
  return [xs, ...ys];
}

/**
 * A time-series chart on uPlot (canvas, small, fast on phones), styled with
 * our tokens in light and dark. The legend is ours: it shows the value under
 * the cursor, or the latest one. `empty` and `error` replace the chart.
 */
export function TimeChart({
  title,
  series,
  format,
  locale,
  loading = false,
  error,
  empty,
}: {
  title: string;
  series: ChartSeries[];
  /** Formats a value with its unit, for the axis and the legend. */
  format: (value: number) => string;
  locale: string;
  loading?: boolean;
  error?: ReactNode;
  empty: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Read through a ref: a new `format` from the parent must not rebuild the chart.
  const formatRef = useRef(format);
  formatRef.current = format;
  const [cursor, setCursor] = useState<number | null>(null);
  const theme = useThemeVersion();
  const data = useMemo(() => align(series), [series]);
  const xs = data[0] as number[];
  const hasData = data.slice(1).some((ys) => (ys as (number | null)[]).some((v) => v !== null));
  const span = xs.length > 1 ? (xs[xs.length - 1] ?? 0) - (xs[0] ?? 0) : 0;

  useEffect(() => {
    const el = box.current;
    if (!el || !hasData) return;
    const muted = token("--muted");
    const line = token("--line");
    const timeFormat = new Intl.DateTimeFormat(
      locale,
      span > 2 * 86_400
        ? { day: "numeric", month: "short" }
        : { hour: "2-digit", minute: "2-digit" },
    );
    const axis = {
      stroke: muted,
      grid: { stroke: line, width: 1 },
      ticks: { stroke: line, width: 1 },
    };
    const chart = new uPlot(
      {
        width: el.clientWidth,
        height: HEIGHT,
        legend: { show: false },
        cursor: { y: false, points: { size: 6 } },
        scales: {
          x: { time: true },
          y: { range: (_u, _min, max) => [0, max > 0 ? max * 1.1 : 1] },
        },
        axes: [
          { ...axis, values: (_u, splits) => splits.map((s) => timeFormat.format(s * 1000)) },
          { ...axis, size: 72, values: (_u, splits) => splits.map((v) => formatRef.current(v)) },
        ],
        series: [
          {},
          ...series.map((s, i) => ({
            label: s.label,
            stroke: token(SERIES_TOKENS[i % SERIES_TOKENS.length] ?? "--accent"),
            width: 1.5,
            points: { show: false },
          })),
        ],
        hooks: { setCursor: [(u) => setCursor(u.cursor.idx ?? null)] },
      },
      data,
      el,
    );
    const resize = new ResizeObserver(() =>
      chart.setSize({ width: el.clientWidth, height: HEIGHT }),
    );
    resize.observe(el);
    return () => {
      resize.disconnect();
      chart.destroy();
    };
  }, [data, hasData, series, locale, span, theme]);

  const valueAt = (i: number) => {
    const ys = data[i + 1] as (number | null)[];
    const idx = cursor ?? ys.findLastIndex((v) => v !== null);
    const value = idx >= 0 ? ys[idx] : null;
    return value === null || value === undefined ? "—" : format(value);
  };

  return (
    <figure className="flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-surface p-4">
      <figcaption className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="text-sm font-semibold">{title}</span>
        {hasData && (
          <span className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
            {series.map((s, i) => (
              <span key={s.label} className="inline-flex items-center gap-1.5">
                <span
                  className="h-0.5 w-3 rounded"
                  style={{ background: `var(${SERIES_TOKENS[i % SERIES_TOKENS.length]})` }}
                  aria-hidden
                />
                {s.label}: <span className="tabular-nums text-text">{valueAt(i)}</span>
              </span>
            ))}
          </span>
        )}
      </figcaption>
      {error ? (
        error
      ) : loading ? (
        <div className="animate-pulse rounded-lg bg-surface-2" style={{ height: HEIGHT }} />
      ) : !hasData ? (
        <div
          className="flex items-center justify-center rounded-lg border border-dashed border-line px-4 text-center text-sm text-muted"
          style={{ height: HEIGHT }}
        >
          {empty}
        </div>
      ) : (
        <div ref={box} role="img" aria-label={title} className="w-full min-w-0" />
      )}
    </figure>
  );
}
